import { CopyableField } from "~/components/CopyableField"
import { TooltipButton } from "~/components/ui/button"
import { Field } from "~/components/ui/field"
import { FieldLabel } from "~/components/ui/field"
import { Dices } from "lucide-react"

import {
	type MouseEvent,
	type TouchEvent,
	useEffect,
	useId,
	useRef,
	useState
} from "react"
import { Button } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Label } from "~/components/ui/label"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { Slider } from "~/components/ui/slider"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs"
import * as ColorUtils from "~/lib/color-utils"

type ColorMode = "hex" | "rgba" | "hsla"

export function ColorPicker({
	id: triggerId,
	disabled = false,
	color = "#000000",
	onChange
}: {
	id?: string
	disabled?: boolean
	color?: string
	onChange?: (value: string) => void
}) {
	const id = useId()
	const [currentColor, setCurrentColor] = useState(color)
	const [hexInput, setHexInput] = useState(color)
	const [colorMode, setColorMode] = useState<ColorMode>("hex")

	const colorPlaneRef = useRef<HTMLDivElement>(null)
	const isDragging = useRef(false)

	const rgb = ColorUtils.hexToRgb(currentColor) || { r: 0, g: 0, b: 0 }
	const hsl = ColorUtils.rgbToHsl(rgb)
	const rgbaString = ColorUtils.formatRgba(rgb)
	const hslaString = ColorUtils.formatHsla(hsl)

	const handleColorChange = (newColor: string) => {
		setCurrentColor(newColor)
		setHexInput(newColor)
		onChange?.(newColor)
	}

	const updateHSL = (h: number, s: number, l: number) => {
		const rgb = ColorUtils.hslToRgb({ h, s, l })
		handleColorChange(ColorUtils.rgbToHex(rgb))
	}

	const handleMouseDown = (e: MouseEvent | TouchEvent) => {
		isDragging.current = true
		if ("touches" in e) {
			e.preventDefault() // Prevent scrolling on touch devices
		}
		handleColorPlaneChange(e)
	}

	const handleMouseUp = (_e: MouseEvent | TouchEvent) => {
		isDragging.current = false
	}

	const handleMouseMove = (e: MouseEvent | TouchEvent) => {
		if (isDragging.current) {
			if ("touches" in e) {
				e.preventDefault() // Prevent scrolling on touch devices
			}
			handleColorPlaneChange(e)
		}
	}

	const handleColorPlaneChange = (e: MouseEvent | TouchEvent) => {
		if (!colorPlaneRef.current) return

		const rect = colorPlaneRef.current.getBoundingClientRect()
		const clientX = "touches" in e ? e.touches[0]?.clientX : e.clientX
		const clientY = "touches" in e ? e.touches[0]?.clientY : e.clientY

		if (clientX === undefined || clientY === undefined) return

		const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
		const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))

		updateHSL(hsl.h, Math.round(x * 100), Math.round((1 - y) * 100))
	}

	useEffect(() => {
		const handleGlobalMouseUp = () => {
			isDragging.current = false
		}

		const handleGlobalTouchEnd = () => {
			isDragging.current = false
		}

		const handleVisibilityChange = () => {
			if (document.hidden) {
				isDragging.current = false
			}
		}

		window.addEventListener("mouseup", handleGlobalMouseUp)
		window.addEventListener("touchend", handleGlobalTouchEnd)
		document.addEventListener("visibilitychange", handleVisibilityChange)

		return () => {
			window.removeEventListener("mouseup", handleGlobalMouseUp)
			window.removeEventListener("touchend", handleGlobalTouchEnd)
			document.removeEventListener("visibilitychange", handleVisibilityChange)
			isDragging.current = false
		}
	}, [])

	useEffect(() => {
		setCurrentColor(color)
		setHexInput(color)
	}, [color])

	const randomColor = () => {
		const randomHex = Math.floor(Math.random() * 0xffffff)
			.toString(16)
			.padStart(6, "0")
		handleColorChange(`#${randomHex}`)
	}

	const handleHexChange = (hex: string) => {
		// Support both #RGB and #RRGGBB formats
		const fullHex = hex.startsWith("#") ? hex : `#${hex}`
		if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(fullHex)) {
			// Convert 3-digit hex to 6-digit
			const normalizedHex =
				fullHex.length === 4
					? `#${fullHex[1]}${fullHex[1]}${fullHex[2]}${fullHex[2]}${fullHex[3]}${fullHex[3]}`
					: fullHex
			handleColorChange(normalizedHex.toLowerCase())
		} else {
			setHexInput(currentColor)
		}
	}

	const handleRgbChange = (key: keyof typeof rgb, value: string) => {
		const numValue = Number.parseInt(value, 10)
		if (!Number.isNaN(numValue) && numValue >= 0 && numValue <= 255) {
			const newRgb = { ...rgb, [key]: numValue }
			handleColorChange(ColorUtils.rgbToHex(newRgb))
		}
	}

	const handleHslChange = (key: keyof typeof hsl, value: string) => {
		const numValue = Number.parseInt(value, 10)
		if (Number.isNaN(numValue)) return

		const max = key === "h" ? 360 : 100
		if (numValue >= 0 && numValue <= max) {
			const newHsl = { ...hsl, [key]: numValue }
			const newRgb = ColorUtils.hslToRgb(newHsl)
			handleColorChange(ColorUtils.rgbToHex(newRgb))
		}
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					id={triggerId}
					variant="outline"
					className="w-full sm:w-60 justify-start text-left font-normal"
					disabled={disabled}
				>
					<div
						className="size-4 shrink-0 rounded-full border border-stroke"
						style={{ backgroundColor: currentColor }}
					/>
					<span className="font-mono text-xs">{currentColor}</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="bg-background p-3">
				<div className="grid gap-3">
					{/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- interactive color picker plane requires div for proper touch/mouse event handling */}
					<div
						ref={colorPlaneRef}
						role="button"
						aria-label="Adjust saturation and lightness with arrow keys"
						tabIndex={0}
						onKeyDown={(event) => {
							if (
								!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
									event.key
								)
							)
								return
							event.preventDefault()
							updateHSL(
								hsl.h,
								Math.max(
									0,
									Math.min(
										100,
										hsl.s +
											(event.key === "ArrowRight"
												? 1
												: event.key === "ArrowLeft"
													? -1
													: 0)
									)
								),
								Math.max(
									0,
									Math.min(
										100,
										hsl.l +
											(event.key === "ArrowUp"
												? 1
												: event.key === "ArrowDown"
													? -1
													: 0)
									)
								)
							)
						}}
						className="relative w-full h-40 rounded-md cursor-crosshair touch-none"
						style={{
							// backgroundColor: `hsl(${hsl.h}, 100%, 50%)`,
							background: `
                linear-gradient(
                  180deg,
                  #fff 0%,
                  rgba(128, 128, 128, 0) 50%,
                  #000 100%
                ),
                radial-gradient(
                  ellipse at 100% 50%,
                  /* Hue at 0%, fade to transparent by 100% */
                  hsl(${hsl.h}, 100%, 50%) 0%,
                  transparent 100%
                )
              `
						}}
						onMouseDown={(e: MouseEvent<HTMLDivElement>) => handleMouseDown(e)}
						onMouseMove={(e: MouseEvent<HTMLDivElement>) => handleMouseMove(e)}
						onMouseUp={(e: MouseEvent<HTMLDivElement>) => handleMouseUp(e)}
						onTouchStart={(e: TouchEvent<HTMLDivElement>) => handleMouseDown(e)}
						onTouchMove={(e: TouchEvent<HTMLDivElement>) => handleMouseMove(e)}
						onTouchEnd={(e: TouchEvent<HTMLDivElement>) => handleMouseUp(e)}
					>
						<div
							className="absolute w-4 h-4 border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none shadow-md z-10"
							style={{
								left: `${hsl.s}%`,
								top: `${100 - hsl.l}%`,
								backgroundColor: currentColor
							}}
						/>
					</div>

					<div className="grid gap-2">
						<Label className="text-xs text-text-small">Hue</Label>
						<div className="relative">
							<Slider
								aria-label="Hue"
								value={[hsl.h]}
								max={360}
								step={1}
								variant="hue"
								onValueChange={([h]) => {
									// Ensure smooth transition at the end of the slider
									const hue = h ?? 0
									updateHSL(hue === 360 ? 359 : hue, hsl.s, hsl.l)
								}}
							/>
						</div>
					</div>

					<Tabs
						value={colorMode}
						onValueChange={(v) => setColorMode(v as ColorMode)}
					>
						<TabsList stretch>
							<TabsTrigger value="hex">Hex</TabsTrigger>
							<TabsTrigger value="rgba">RGBA</TabsTrigger>
							<TabsTrigger value="hsla">HSLA</TabsTrigger>
						</TabsList>

						<TabsContent value="hex" className="mt-2">
							<div
								className="flex items-center gap-2"
								onBlur={() => handleHexChange(hexInput)}
							>
								<CopyableField
									value={hexInput}
									onChange={(value) => {
										setHexInput(value)
										if (/^#?[\da-f]{6}$/i.test(value)) handleHexChange(value)
									}}
									label="Hex color"
								/>
								<TooltipButton
									variant="ghost"
									tooltip="Random color"
									onClick={randomColor}
								>
									<Dices />
								</TooltipButton>
							</div>
						</TabsContent>

						<TabsContent value="rgba" className="mt-2">
							<div className="grid gap-4">
								<CopyableField value={rgbaString} label="RGBA color" />
								<div className="grid grid-cols-4 gap-2">
									{(["r", "g", "b", "a"] as const).map((channel) => (
										<Field key={channel}>
											<FieldLabel htmlFor={id + "-rgba-" + channel}>
												{channel.toUpperCase()}
											</FieldLabel>
											<Input
												id={id + "-rgba-" + channel}
												value={channel === "a" ? "1" : rgb[channel]}
												readOnly={channel === "a"}
												onChange={(event) => {
													if (channel !== "a")
														handleRgbChange(channel, event.target.value)
												}}
												className="font-mono"
											/>
										</Field>
									))}
								</div>
							</div>
						</TabsContent>

						<TabsContent value="hsla" className="mt-2">
							<div className="grid gap-4">
								<CopyableField value={hslaString} label="HSLA color" />
								<div className="grid grid-cols-4 gap-2">
									{(["h", "s", "l", "a"] as const).map((channel) => (
										<Field key={channel}>
											<FieldLabel htmlFor={id + "-hsla-" + channel}>
												{channel.toUpperCase()}
											</FieldLabel>
											<Input
												id={id + "-hsla-" + channel}
												value={channel === "a" ? "1" : hsl[channel]}
												readOnly={channel === "a"}
												onChange={(event) => {
													if (channel !== "a")
														handleHslChange(channel, event.target.value)
												}}
												className="font-mono"
											/>
										</Field>
									))}
								</div>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</PopoverContent>
		</Popover>
	)
}
